import ControlViewModel from '@/ViewModels/ControlViewModel';
import TabStrip2ViewModelT from '@/ViewModels/Controls/TabStrip2ViewModel';
import TabStripTab2ViewModelT from '@/ViewModels/Controls/TabStripTab2ViewModel';
import LinkViewModelT from '@/ViewModels/Controls/LinkViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import ComponentManager from '@/Management/ComponentManager';
import { ITabGroupBoxViewModelConstructorParameter } from '#/DefaultViewExtensionInterfaces';
import Context from '@/ViewModels/Context';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import { IDetailViewGroupDTO } from "coreframetypings/dto";

export default class TabGroupBoxViewModel extends ControlViewModel {
    /**
    * Gibt an, ob es sich um die erste TabGroup handelt
    */
    public isFirstTabGroup: boolean;

    /**
    * Der Kontext des ViewModels
    */
    public context: Context;

    public layout: string;

    /**
    * Bestimmt den aktiven Tab
    */
    public selectedIndex: KnockoutObservable<number>;

    /**
    * Enthält die DetailViewGroup
    */
    public detailViewGroup: IDetailViewGroupDTO;

    /**
    * Enthält die inneren DetailViewGroups
    */
    public detailViewGroups: IDetailViewGroupDTO[];

    /**
    * Enthält den TabStrip
    */
    public tabStrip: TabStrip2ViewModelT;

    /**
    * Gibt den ersten Gruppennamen der verketteten Gruppen an.
    */
    public firstGroupName: any;

    /**
    * Bestimmt, ob eine Sortierung per Drag&Drop möglich ist.
    */
    public isSortable: any;

    public isVisible: any;

    public tab: TabStripTab2ViewModelT;

    private TabStripViewModel = ComponentManager.getViewModel<typeof TabStrip2ViewModelT>('TabStrip2ViewModel');
    private TabStripTabViewModel = ComponentManager.getViewModel<typeof TabStripTab2ViewModelT>('TabStripTab2ViewModel');
    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');

    constructor(protected params: ITabGroupBoxViewModelConstructorParameter) {
        super(params);

        this.undisposeProperties.push('parent');

        this.isFirstTabGroup = this.paramOrDefault(params.isFirstTabGroup, params.tabStrip == null);
        this.context = this.paramOrDefault(params.context, null);
        this.layout = this.paramOrDefault(params.layout, 'NoneLayout');
        this.selectedIndex = this.observableOrDefault(params.selectedIndex(), 0); // Das Observable wird hier absichtlich aufgelöst, um keine Bindung zu erzeugen. Der Wert soll hier nur bei der Initialisierung einmalig ausgelesen werden. Das Observable wird sich sonst nämlich von mehreren TabGroupBoxViewModels geteilt.
        this.firstGroupName = this.paramOrDefault(params.firstGroupName, null);
        this.detailViewGroup = this.paramOrDefault(params.detailViewGroup, null);
        this.detailViewGroups = this.paramOrDefault(params.detailViewGroups, []);
        this.isSortable = this.observableOrDefault(params.isSortable, false);
        this.tabStrip = this.paramOrDefault(params.tabStrip, () => this.createTabStrip());

        if (this.context && this.context instanceof DetailViewContext && this.context.isSummaryViewMode())
            this.layout = "OneColumnLayout";

        this.tab = this.createTab();

        // Reihenfolge wichtig: Der Tab bekommt das aktuelle "isVisible" übergeben, damit die Sichtbarkeit "von außen"
        // steuerbar ist. Erst anschließend übernehmen wir "isVisible" vom Tabstrip, da sonst die komplette Box mit
        // allen Reiterkarten ausgeblendet wird.
        if (this.isFirstTabGroup)
            this.isVisible = this.tabStrip.isVisible;

        this.addToTabstrip(this.tab, params.parent, params.tabIndex);
    }

    private createTabStrip(): TabStrip2ViewModelT {
        return new this.TabStripViewModel({
            selectedIndex: this.selectedIndex,
            isSummaryViewMode: this.isSummaryViewMode,
            isSortable: this.isSortable,
            firstGroupName: this.firstGroupName
        });
    }

    private createTab(): TabStripTab2ViewModelT {
        const tab = new this.TabStripTabViewModel({
            context: this.context,
            technicalName: this.detailViewGroup.name,
            parentLocator: this.locator,
            locator: this.detailViewGroup.name,
            isVisible: this.isVisible,
            detailViewItems: this.detailViewGroup.items,
            isSummaryViewMode: this.isSummaryViewMode,
            detailViewGroups: (() => {
                let groups = [];
                if (this.detailViewGroup.detailViewGroups != null) {
                    for (let i = 0; i < this.detailViewGroup.detailViewGroups.length; i++) {
                        let detailViewGroup = this.detailViewGroup.detailViewGroups[i];
                        if (detailViewGroup.uiGenerator.indexOf('TabGroup') === -1) {
                            groups.push(detailViewGroup);
                        }
                    }
                }
                return groups;
            })(),
            layout: this.layout,
            link: new this.LinkViewModel({
                parentLocator: this.locator,
                locator: this.detailViewGroup.name + ".link",
                label: new this.LabelViewModel({
                    parentLocator: this.locator,
                    locator: this.detailViewGroup.name + ".link.label",
                    ergName: this.detailViewGroup.ergName
                }),
                onClick: () => {
                    this.tabStrip.selected(tab);
                    let selectedIndex = this.tabStrip.selectedIndex();
                    this.params.selectedIndex(selectedIndex);
                }
            }),
            tabStrip: this.tabStrip
        });
        return tab;
    }

    private addToTabstrip(tab: TabStripTab2ViewModelT, parent: TabGroupBoxViewModel, tabIndex: number) {
        if (!parent || !parent.tab || tabIndex < 0) {
            this.tabStrip.tabs.push(tab);
            return;
        }

        const parentTabIndex = this.tabStrip.tabs.indexOf(parent.tab);
        if (parentTabIndex < 0) {
            this.tabStrip.tabs.push(tab);
            return;
        }

        const index = parentTabIndex + tabIndex + 1;
        const tabs = [...this.tabStrip.tabs()];
        tabs.splice(index, 0, tab);
        this.tabStrip.tabs(tabs);
    }
}

ComponentManager.setViewModel('TabGroupBoxViewModel', TabGroupBoxViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');